import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { quotationsApi as Q, QUOTE_STATUS_STYLE, inr } from "@/lib/quotations";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, Eye, CheckCircle2, XCircle, Clock, Pencil, ShoppingCart } from "lucide-react";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const canEdit = can("quotations", "edit");

  const load = async () => {
    setLoading(true);
    try { setQ(await Q.get(id)); } catch { toast.error("Not found"); navigate("/quotations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const move = async (status, note = null) => {
    try { setQ(await Q.setStatus(id, status, note)); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const submitReject = async () => {
    if (!rejectReason) { toast.error("Enter a reason"); return; }
    await move("Rejected", rejectReason); setRejectOpen(false); setRejectReason("");
  };

  if (loading || !q) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const t = q.totals || {};
  const c = q.customer || {};

  return (
    <div className="space-y-6" data-testid="quotation-detail-page">
      <button onClick={() => navigate("/quotations")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to quotations</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{q.quote_no}</h2>
            <Badge className={`hover:bg-inherit ${QUOTE_STATUS_STYLE[q.status]}`} data-testid="quote-status-badge">{q.status}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">{c.company_name} · created {new Date(q.created_at).toLocaleDateString()}{q.enquiry_no ? ` · from ${q.enquiry_no}` : ""}</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {q.status === "Draft" && <Button variant="outline" onClick={() => navigate(`/quotations/${id}/edit`)} data-testid="edit-quotation-btn"><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
            {q.status === "Draft" && <Button variant="outline" onClick={() => move("Sent")} data-testid="send-quotation-btn"><Send className="mr-2 h-4 w-4" />Mark Sent</Button>}
            {q.status === "Sent" && <Button variant="outline" onClick={() => move("Customer Review")} data-testid="review-quotation-btn"><Eye className="mr-2 h-4 w-4" />Customer Review</Button>}
            {["Sent", "Customer Review"].includes(q.status) && <Button variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => move("Accepted")} data-testid="accept-quotation-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Accept</Button>}
            {["Sent", "Customer Review"].includes(q.status) && <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setRejectOpen(true)} data-testid="reject-quotation-btn"><XCircle className="mr-2 h-4 w-4" />Reject</Button>}
            {["Draft", "Sent", "Customer Review"].includes(q.status) && <Button variant="outline" onClick={() => move("Expired")} data-testid="expire-quotation-btn"><Clock className="mr-2 h-4 w-4" />Mark Expired</Button>}
            {q.status === "Accepted" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => navigate(`/sales-orders/new?quotation=${q.id}`)} disabled={!!q.sales_order_id} title={q.sales_order_id ? "Already converted to a Sales Order" : ""} data-testid="convert-so-btn"><ShoppingCart className="mr-2 h-4 w-4" />{q.sales_order_id ? "Converted to SO" : "Convert to Sales Order"}</Button>}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Line items</h3>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Fabric / Print</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Specs</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Qty</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Rate</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Disc</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">GST</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(q.items || []).map((it, i) => (
                  <TableRow key={it.id || i} data-testid={`detail-line-${i}`}>
                    <TableCell className="font-medium text-slate-800">{it.item_name || it.fabric_type}<span className="block text-xs text-slate-400">{[it.product_code, it.fabric_type && it.item_name ? it.fabric_type : null, it.print_type, it.size].filter(Boolean).join(" · ")}</span></TableCell>
                    <TableCell className="text-slate-600">{[it.gsm && `${it.gsm} GSM`, it.width, it.fabric_required ? `${it.fabric_required} m/unit` : null].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-slate-600">{it.quantity} {it.unit}</TableCell>
                    <TableCell className="tabular-nums text-slate-600">{inr(it.rate)}</TableCell>
                    <TableCell className="text-slate-600">{it.discount_pct || 0}%</TableCell>
                    <TableCell className="text-slate-600">{it.tax_pct || 0}%</TableCell>
                    <TableCell className="tabular-nums font-medium text-slate-800">{inr(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="tabular-nums">{inr(t.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Discount</span><span className="tabular-nums">− {inr(t.total_discount)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Taxable value</span><span className="tabular-nums">{inr(t.taxable_value)}</span></div>
              {q.tax_mode === "intra" ? (
                <>
                  <div className="flex justify-between text-slate-600"><span>CGST</span><span className="tabular-nums">{inr(t.cgst)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>SGST</span><span className="tabular-nums">{inr(t.sgst)}</span></div>
                </>
              ) : (
                <div className="flex justify-between text-slate-600"><span>IGST</span><span className="tabular-nums">{inr(t.igst)}</span></div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900"><span>Grand total</span><span className="tabular-nums" data-testid="detail-grand-total">{inr(t.grand_total)}</span></div>
            </div>
          </div>
          {(q.terms || q.customer_notes) && (
            <div className="mt-4 space-y-2 text-sm">
              {q.terms && <p className="rounded-md bg-slate-50 p-3 text-slate-600"><span className="font-medium">Terms: </span>{q.terms}</p>}
              {q.customer_notes && <p className="rounded-md bg-slate-50 p-3 text-slate-600"><span className="font-medium">Note to customer: </span>{q.customer_notes}</p>}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Customer</h3>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Company</dt><dd className="font-medium text-slate-800">{c.company_name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Code</dt><dd className="text-slate-700">{c.customer_code || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">GSTIN</dt><dd className="text-slate-700">{c.gstin || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Contact</dt><dd className="text-slate-700">{c.contact?.name || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Place of supply</dt><dd className="text-slate-700">{q.place_of_supply || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Payment terms</dt><dd className="text-slate-700">{c.payment_terms || "—"}</dd></div>
            </dl>
            <button className="mt-3 text-xs text-indigo-600 hover:underline" onClick={() => navigate(`/customers/${q.customer_id}`)}>View customer →</button>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Artwork</h3>
            {q.artwork ? (
              <div className="mt-3 text-sm">
                <p className="font-medium text-slate-800">{q.artwork.artwork_code} · {q.artwork.name}</p>
                <p className="text-slate-500">Locked V{q.artwork.version}</p>
                <button className="mt-2 text-xs text-indigo-600 hover:underline" onClick={() => navigate(`/artwork/${q.artwork_id}`)}>View artwork →</button>
              </div>
            ) : <p className="mt-3 text-sm text-slate-400">No artwork attached (not finalized).</p>}
            <p className="mt-4 text-xs text-slate-500">Validity: <span className="font-medium text-slate-700">{q.validity_date || "—"}</span></p>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">Status history</h3>
        <div className="mt-4 space-y-3">
          {[...(q.history || [])].reverse().map((h, i) => (
            <div key={i} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="history-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div>
                <p className="text-sm text-slate-700"><span className="font-medium">{h.status}</span>{h.note ? ` · ${h.note}` : ""}</p>
                <p className="text-xs text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject quotation</DialogTitle>
            <DialogDescription>Record why the customer rejected this quotation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={submitReject} data-testid="confirm-reject-btn" className="bg-rose-600 text-white hover:bg-rose-700">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
